import { z } from "zod";
import dayjs, { Dayjs } from "dayjs";
import i18n from "@/i18n";
import { refinements } from "@helpers/refinements";

// Schema for batch number validation
export const batchNumberSchema = z.object({
    batch_number: z.string().nonempty(),
    expiration_date: z.instanceof(dayjs as unknown as typeof Dayjs)
        .refine(refinements.validDate.refine, refinements.validDate.message)
        .nullish()
});

// Schema for product information
export const productSchema = z.object({
    id: z.number(),
    name: z.string(),
    code: z.string()
});

// Schema for line items with batch number validation
export const lineSchema = z.object({
    id: z.number(),
    comment: z.string().nullish(),
    product: productSchema,
    picked_quantity: z.coerce.number(),
    batch_numbers: z.array(batchNumberSchema)
}).refine(
    scheme => scheme.batch_numbers.length <= scheme.picked_quantity,
    {
        message: i18n.t("sales.picking.modals.batchNumbers.validation.cannotBeGreaterThanPickedQuantity"),
        path: ["batch_numbers"]
    }
);

// Main form schema with additional validation for duplicate batch numbers
export const formScheme = z.object({
    lines: z.array(lineSchema)
}).superRefine((scheme, ctx) => {
    // First, create a mapping of batch numbers to their paths
    const batchNumberPaths = scheme.lines.reduce((acc, line, lineIndex) => {
        line.batch_numbers.forEach((batch, batchIndex) => {
            const batchNumber = batch.batch_number;
            const path = [lineIndex, "batch_numbers", batchIndex];

            if (!acc[batchNumber]) {
                acc[batchNumber] = [];
            }

            acc[batchNumber].push(path);
        });

        return acc;
    }, {} as Record<string, (string | number)[][]>);

    // Find duplicate batch numbers and add errors
    Object.entries(batchNumberPaths).forEach(([batchNumber, paths]) => {
        if (paths.length > 1) {
            // Add error for each duplicate batch number
            paths.forEach(path => {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    params: {
                        i18n: {
                            key: "sales.picking.modals.batchNumbers.validation.batchNumberDuplicate"
                        }
                    },
                    path: [...path, "batch_number"]
                });
            });
        }
    });
});

// Type for the form schema
export type BatchNumbersScheme = z.infer<typeof formScheme>;